import { ClassroomCardSkeleton } from "@/components/skeletons";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { useSchools } from "@/lib/hooks/schools/useSchool";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import { LoadMoreButton, SearchBar } from "@ai-teach/ui-web";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { HiArrowRight } from "react-icons/hi";

const PAGE_SIZE = 24;

/** The way into the classrooms: too many to list flat, so they are reached through their school. */
export function AdminSchoolsPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebouncedSearch(searchInput);
  const { schools, loading, hasMore, total, loadMore } = useSchools(searchText, PAGE_SIZE);

  return (
    <TeacherPage title="Schools" embedded actions={<StatPill>{total} total</StatPill>}>
      <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search by school" />

      {loading && schools.length === 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {["a", "b", "c", "d"].map((key) => (
            <ClassroomCardSkeleton key={key} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {schools.map((school) => (
            <button
              key={school.id}
              type="button"
              onClick={() =>
                navigate({ to: "/admin/schools/$schoolId", params: { schoolId: school.id } })
              }
              className="app-card group flex items-center gap-2 p-5 text-left"
            >
              <span className="app-title flex-1 truncate">{school.name}</span>
              <HiArrowRight className="h-4 w-4 text-app-ink transition-transform group-hover:translate-x-1" />
            </button>
          ))}

          {schools.length === 0 && (
            <p className="app-card body-sm p-6 text-app-muted">No schools match that search.</p>
          )}
        </div>
      )}

      <LoadMoreButton hasMore={hasMore} loading={loading} onClick={loadMore} />

      <p className="body-sm text-center text-app-ink/70">
        Know the classroom but not the school?{" "}
        <Link to="/admin/classrooms" className="font-semibold text-app-ink hover:underline">
          Search every classroom
        </Link>
      </p>
    </TeacherPage>
  );
}
